import {
  actionProgress,
  canEditField,
  deliveredPct,
  TASK_EVENT_LABEL,
  TASK_STATUS_LABEL,
  taskAccess,
  taskFlow,
  weekLabel,
  type TaskPriority,
  type TaskStatus,
} from '@supply/contracts'
import { CheckCircle2, Eye, Pencil, Play, RotateCcw, Save, Trash2, UserRoundCog, XCircle } from 'lucide-react'
import * as React from 'react'

import { DatePicker } from '@/components/date-picker'
import { AreaTag, DueBadge, fmtQty, PriorityTag, TaskStatusPill, useParam } from '@/components/domain'
import { ErrorState, ListSkeleton } from '@/components/states'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  actionsForTasks,
  decisionsForTasks,
  restoreTask,
  taskByKey,
  taskComments,
  taskEvents,
  trashTask,
  updateTask,
  type TaskPatch,
} from '@/data/api'
import type { Task, TaskEvent } from '@/data/types'
import { invalidate, useLoad } from '@/data/use-load'
import { formatDateTime } from '@/lib/format'
import { useMemberName, useSession } from '@/lib/session'
import { useWrite } from '@/lib/use-write'
import { cn } from '@/lib/utils'

import { CommentsPanel } from './CommentsPanel'
import { AreaSelect, ExecutorField, PrioritySelect, useWritableAreas, type ExecutorValue } from './fields'
import { MemberAvatar } from './member-avatar'
import { Q3Panel } from './Q3Panel'
import { useStatusMove } from './status-move'
import { TaskActionsPanel } from './TaskActionsPanel'

/**
 * Detalhe da tarefa como painel lateral, aberto por `?tarefa=SUP-12` em
 * qualquer tela (deep link — padrão do Hub SISTEMAS). Montado uma vez, no App.
 */
export function TaskDetailHost() {
  const [key, setKey] = useParam('tarefa')
  return (
    <Sheet open={!!key} onOpenChange={(o) => !o && setKey(null)}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-2xl">
        {key ? <TaskDetail taskKey={key} onClose={() => setKey(null)} /> : null}
      </SheetContent>
    </Sheet>
  )
}

async function loadBundle(key: string) {
  const task = await taskByKey(key.toUpperCase())
  if (!task) return null
  const [actions, decisions, comments, events] = await Promise.all([
    actionsForTasks([task.id]),
    decisionsForTasks([task.id]),
    taskComments(task.id),
    taskEvents(task.id),
  ])
  return { task, actions, decisions, comments, events }
}

function TaskDetail({ taskKey, onClose }: { taskKey: string; onClose: () => void }) {
  const state = useLoad(() => loadBundle(taskKey), [taskKey])

  if (state.error && !state.data) {
    return (
      <div className="p-6">
        <SheetTitle className="sr-only">Tarefa {taskKey}</SheetTitle>
        <ErrorState description={state.error} onRetry={state.reload} />
      </div>
    )
  }
  if (state.data === undefined) {
    return (
      <div className="p-6">
        <SheetTitle className="sr-only">Carregando {taskKey}</SheetTitle>
        <ListSkeleton rows={6} />
      </div>
    )
  }
  if (state.data === null) {
    return (
      <div className="p-6">
        <SheetTitle>Tarefa não encontrada</SheetTitle>
        <p className="mt-2 text-sm text-muted-foreground">
          {taskKey} não existe ou está na lixeira de outra área.
        </p>
        <Button className="mt-4" variant="outline" onClick={onClose}>
          Fechar
        </Button>
      </div>
    )
  }
  return <TaskDetailBody bundle={state.data} onClose={onClose} />
}

function TaskDetailBody({
  bundle,
  onClose,
}: {
  bundle: NonNullable<Awaited<ReturnType<typeof loadBundle>>>
  onClose: () => void
}) {
  const { task, actions, decisions, comments, events } = bundle
  const { me, areaById, memberById, responsavelOf } = useSession()
  const name = useMemberName()
  const access = taskAccess(me, task)
  const canStatus = canEditField(access, 'status')
  const { run, pending } = useWrite()
  const { confirm, confirmDialog } = useConfirm()
  const move = useStatusMove()
  const [tab, setTab] = React.useState('detalhes')
  const [editing, setEditing] = React.useState(false)
  const trashed = task.deleted_at !== null
  const busy = pending || move.pending

  const flow = taskFlow(task.status)
  const responsavel = task.responsavel_id ? memberById.get(task.responsavel_id) : responsavelOf.get(task.area_id)
  const executor = task.executor_id ? memberById.get(task.executor_id) : undefined
  const executorName = executor?.full_name ?? task.executor_text?.trim() ?? null
  const progress = actionProgress(actions)
  const moveHint = canStatus ? undefined : 'Só a área da tarefa, o executor ou o gestor mudam o status.'
  const canWork = canStatus && !trashed && !busy

  return (
    <div className="flex min-h-full flex-col">
      <SheetHeader className="space-y-2 border-b border-border p-5 text-left">
        <div className="flex flex-wrap items-center gap-2 pr-8">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">{task.key}</span>
          <AreaTag area={areaById.get(task.area_id)} />
          <TaskStatusPill status={task.status} />
          <DueBadge prazo={task.prazo} status={task.status} />
          {access === 'leitura' && (
            <Badge variant="outline" className="gap-1">
              <Eye className="h-3 w-3" /> Somente leitura
            </Badge>
          )}
          {access === 'executor' && (
            <Badge variant="outline" className="gap-1">
              <UserRoundCog className="h-3 w-3" /> Você executa
            </Badge>
          )}
          {trashed && <Badge variant="destructive">Na lixeira</Badge>}
        </div>
        <SheetTitle className="text-xl leading-snug">{task.title}</SheetTitle>
        <SheetDescription>
          Lançada na {weekLabel(task.semana_lancamento).toLowerCase()} por {name(task.created_by, 'alguém')}
        </SheetDescription>
      </SheetHeader>

      {/* Quem, quando, quanto — de relance, sem abrir formulário. */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-border p-5 text-sm sm:grid-cols-3">
        <div className="min-w-0">
          <dt className={DT}>Responsável</dt>
          <dd className="mt-1 flex min-w-0 items-center gap-2">
            {responsavel ? (
              <>
                <MemberAvatar member={responsavel} size="md" />
                <span className="truncate">{responsavel.full_name}</span>
              </>
            ) : (
              <span className="text-muted-foreground">Sem responsável</span>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className={DT}>Executor</dt>
          <dd className="mt-1 flex min-w-0 items-center gap-2">
            <MemberAvatar member={executor} text={task.executor_text} size="md" />
            <span className={cn('truncate', !executorName && 'text-muted-foreground')}>{executorName ?? 'Sem executor'}</span>
          </dd>
        </div>
        <div>
          <dt className={DT}>Prazo</dt>
          <dd className="mt-1">
            {task.prazo ? (
              <span className="font-mono tabular-nums">{formatDateBr(task.prazo)}</span>
            ) : (
              <span className="text-muted-foreground">Sem prazo</span>
            )}
          </dd>
        </div>
        <div>
          <dt className={DT}>Prioridade</dt>
          <dd className="mt-1">
            <PriorityTag priority={task.priority} />
          </dd>
        </div>
        <div>
          <dt className={DT}>Entregue</dt>
          <dd className="mt-1 space-y-1">
            <span className="font-mono tabular-nums">
              {fmtQty(task.qtd_entregue)} de {fmtQty(task.qtd_prevista)} un.
            </span>
            <Progress value={deliveredPct(task.qtd_prevista, task.qtd_entregue)} className="h-1" aria-label="Percentual entregue" />
          </dd>
        </div>
        <div>
          <dt className={DT}>{task.semana_conclusao ? 'Fechada' : 'Ações'}</dt>
          <dd className="mt-1">
            {task.semana_conclusao ? (
              weekLabel(task.semana_conclusao)
            ) : (
              <span>
                <span className="font-mono tabular-nums">
                  {progress.done}/{progress.total}
                </span>
                <span className="text-muted-foreground"> feitas</span>
              </span>
            )}
          </dd>
        </div>
      </dl>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 p-5">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
          <TabsTrigger value="acoes">
            Ações <span className="ml-1 font-mono text-[11px] tabular-nums opacity-70">{actions.length}</span>
          </TabsTrigger>
          <TabsTrigger value="q3">Q3</TabsTrigger>
          <TabsTrigger value="comentarios">
            Comentários <span className="ml-1 font-mono text-[11px] tabular-nums opacity-70">{comments.length}</span>
          </TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>
        <TabsContent value="detalhes">
          {editing ? (
            <TaskFieldsForm task={task} access={access} disabled={trashed} onDone={() => setEditing(false)} />
          ) : (
            <TaskReadView task={task} />
          )}
        </TabsContent>
        <TabsContent value="acoes">
          <TaskActionsPanel
            task={task}
            actions={actions}
            canEdit={access !== 'leitura' && !trashed}
            canChangeStatus={canStatus && !trashed}
            onApplyStatus={(s) => move.request(task, s)}
          />
        </TabsContent>
        <TabsContent value="q3">
          <Q3Panel
            task={task}
            decisions={decisions}
            canEditQ3={canEditField(access, 'q3_bom') && !trashed}
            canEditDecisions={access !== 'leitura' && !trashed}
          />
        </TabsContent>
        <TabsContent value="comentarios">
          <CommentsPanel taskId={task.id} comments={comments} />
        </TabsContent>
        <TabsContent value="historico">
          <History events={events} />
        </TabsContent>
      </Tabs>

      {/* Andar com a tarefa (molde do detalhe do Hub SISTEMAS): o próximo passo
          à direita; editar e excluir à esquerda. */}
      <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-border bg-background p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:px-5">
        {access !== 'leitura' && !trashed && !editing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setTab('detalhes')
              setEditing(true)
            }}
          >
            <Pencil /> Editar
          </Button>
        )}
        {access === 'total' &&
          (trashed ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void run(restoreTask(task.id), { success: 'Tarefa restaurada' }).then((r) => r.ok && invalidate())}
            >
              <RotateCcw /> Restaurar
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              disabled={busy}
              onClick={() =>
                void confirm({
                  title: `Mandar ${task.key} para a lixeira?`,
                  description: 'A área e o gestor podem restaurar depois. Ações, Q3 e comentários são mantidos.',
                  confirmLabel: 'Excluir',
                  destructive: true,
                }).then(async (ok) => {
                  if (!ok) return
                  const r = await run(trashTask(task.id), { success: `${task.key} foi para a lixeira` })
                  if (r.ok) {
                    invalidate()
                    onClose()
                  }
                })
              }
            >
              <Trash2 /> Excluir
            </Button>
          ))}
        {!trashed && (
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {flow.cancel && (
              <Button type="button" variant="ghost" size="sm" disabled={!canWork} title={moveHint} onClick={() => move.request(task, 'cancelada')}>
                <XCircle /> Cancelar
              </Button>
            )}
            {flow.reopen && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canWork}
                title={moveHint ?? `Volta para ${TASK_STATUS_LABEL[flow.reopen]}`}
                onClick={() => flow.reopen && move.request(task, flow.reopen)}
              >
                <RotateCcw /> Reabrir
              </Button>
            )}
            {flow.advance && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canWork}
                title={moveHint ?? `Passa para ${TASK_STATUS_LABEL[flow.advance]}`}
                onClick={() => flow.advance && move.request(task, flow.advance)}
              >
                <Play /> Iniciar
              </Button>
            )}
            {flow.conclude && (
              <Button type="button" size="sm" disabled={!canWork} title={moveHint} onClick={() => move.request(task, 'realizada')}>
                <CheckCircle2 /> Concluir
              </Button>
            )}
          </div>
        )}
      </div>
      {move.dialogs}
      {confirmDialog}
    </div>
  )
}

const DT = 'text-[11px] font-medium uppercase tracking-wider text-muted-foreground'

function formatDateBr(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

/** Ver, não editar: o clique no card abre isto; mudar campos é no "Editar". */
function TaskReadView({ task }: { task: Task }) {
  return (
    <div className="space-y-5 text-sm">
      <section className="space-y-1.5">
        <h3 className={DT}>Detalhes</h3>
        {task.description ? (
          <p className="whitespace-pre-wrap leading-relaxed">{task.description}</p>
        ) : (
          <p className="text-muted-foreground">Sem detalhes.</p>
        )}
      </section>
      <section className="space-y-1.5">
        <h3 className={DT}>Observação do status</h3>
        {task.status_note ? (
          <p className="whitespace-pre-wrap leading-relaxed">{task.status_note}</p>
        ) : (
          <p className="text-muted-foreground">Sem observação.</p>
        )}
      </section>
      {task.status === 'cancelada' && (
        <section className="space-y-1.5">
          <h3 className={DT}>Motivo do cancelamento</h3>
          {task.cancel_reason ? (
            <p className="whitespace-pre-wrap leading-relaxed">{task.cancel_reason}</p>
          ) : (
            <p className="text-muted-foreground">Não informado.</p>
          )}
        </section>
      )}
    </div>
  )
}

interface FieldsState {
  title: string
  description: string
  area_id: string
  executor: ExecutorValue
  prazo: string
  priority: TaskPriority
  qtd_prevista: string
  qtd_entregue: string
  status_note: string
  cancel_reason: string
}

function fromTask(t: Task): FieldsState {
  return {
    title: t.title,
    description: t.description ?? '',
    area_id: t.area_id,
    executor: { executor_id: t.executor_id, executor_text: t.executor_text },
    prazo: t.prazo ?? '',
    priority: t.priority,
    qtd_prevista: String(t.qtd_prevista),
    qtd_entregue: String(t.qtd_entregue),
    status_note: t.status_note ?? '',
    cancel_reason: t.cancel_reason ?? '',
  }
}

/** Campos editáveis conforme o acesso (o banco confere de novo). */
function TaskFieldsForm({
  task,
  access,
  disabled,
  onDone,
}: {
  task: Task
  access: ReturnType<typeof taskAccess>
  disabled: boolean
  /** Salvou ou descartou: volta para a leitura. */
  onDone: () => void
}) {
  const [v, setV] = React.useState<FieldsState>(() => fromTask(task))
  React.useEffect(() => setV(fromTask(task)), [task])
  const { run, pending } = useWrite()
  const writable = useWritableAreas()
  const { areaById } = useSession()
  const current = areaById.get(task.area_id)
  // A área atual sempre aparece no seletor, mesmo para quem não escreve nela.
  const areas = writable.some((a) => a.id === task.area_id) || !current ? writable : [current, ...writable]
  const can = (f: string) => !disabled && canEditField(access, f)

  const num = (s: string) => Number(s.replace(',', '.'))
  const patch: TaskPatch = {}
  if (v.title.trim() !== task.title) patch.title = v.title.trim()
  if ((v.description.trim() || null) !== task.description) patch.description = v.description.trim() || null
  if (v.area_id !== task.area_id) patch.area_id = v.area_id
  if (v.executor.executor_id !== task.executor_id) patch.executor_id = v.executor.executor_id
  if ((v.executor.executor_text?.trim() || null) !== task.executor_text) patch.executor_text = v.executor.executor_text?.trim() || null
  if ((v.prazo || null) !== task.prazo) patch.prazo = v.prazo || null
  if (v.priority !== task.priority) patch.priority = v.priority
  if (num(v.qtd_prevista) !== task.qtd_prevista) patch.qtd_prevista = num(v.qtd_prevista)
  if (num(v.qtd_entregue) !== task.qtd_entregue) patch.qtd_entregue = num(v.qtd_entregue)
  if ((v.status_note.trim() || null) !== task.status_note) patch.status_note = v.status_note.trim() || null
  if ((v.cancel_reason.trim() || null) !== task.cancel_reason) patch.cancel_reason = v.cancel_reason.trim() || null
  const dirty = Object.keys(patch).length > 0

  const qtdOk = Number.isFinite(num(v.qtd_prevista)) && num(v.qtd_prevista) > 0
  const entOk = Number.isFinite(num(v.qtd_entregue)) && num(v.qtd_entregue) >= 0 && num(v.qtd_entregue) <= num(v.qtd_prevista)
  const valid = v.title.trim().length > 0 && qtdOk && entOk

  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (!dirty || !valid) return
        void run(updateTask(task.id, patch), { success: 'Tarefa salva' }).then((r) => {
          if (!r.ok) return
          invalidate()
          onDone()
        })
      }}
    >
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="f-title">Tarefa</Label>
        <Input id="f-title" value={v.title} disabled={!can('title')} onChange={(e) => setV({ ...v, title: e.target.value })} aria-invalid={!v.title.trim()} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-area">Área</Label>
        <AreaSelect id="f-area" value={v.area_id} onChange={(id) => setV({ ...v, area_id: id })} areas={areas} disabled={!can('area_id')} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-priority">Prioridade</Label>
        <PrioritySelect id="f-priority" value={v.priority} onChange={(p) => setV({ ...v, priority: p })} disabled={!can('priority')} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-exec">Executor</Label>
        <ExecutorField id="f-exec" value={v.executor} onChange={(e) => setV({ ...v, executor: e })} areaId={v.area_id} disabled={!can('executor_id')} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-prazo">Prazo</Label>
        <DatePicker id="f-prazo" title="Prazo" value={v.prazo} disabled={!can('prazo')} onChange={(prazo) => setV({ ...v, prazo })} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-qtd">Quantidade prevista</Label>
        <Input id="f-qtd" inputMode="decimal" className="font-mono tabular-nums" value={v.qtd_prevista} disabled={!can('qtd_prevista')} onChange={(e) => setV({ ...v, qtd_prevista: e.target.value })} aria-invalid={!qtdOk} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="f-ent">Quantidade entregue</Label>
        <Input id="f-ent" inputMode="decimal" className="font-mono tabular-nums" value={v.qtd_entregue} disabled={!can('qtd_entregue')} onChange={(e) => setV({ ...v, qtd_entregue: e.target.value })} aria-invalid={!entOk} />
        {!entOk && <p className="text-xs text-destructive">Entre 0 e a quantidade prevista.</p>}
      </div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="f-note">Observação do status</Label>
        <Textarea id="f-note" rows={2} value={v.status_note} disabled={!can('status_note')} onChange={(e) => setV({ ...v, status_note: e.target.value })} />
      </div>
      {task.status === 'cancelada' && (
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="f-cancel">Motivo do cancelamento</Label>
          <Textarea id="f-cancel" rows={2} value={v.cancel_reason} disabled={!can('cancel_reason')} onChange={(e) => setV({ ...v, cancel_reason: e.target.value })} />
        </div>
      )}
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="f-desc">Detalhes</Label>
        <Textarea id="f-desc" rows={4} value={v.description} disabled={!can('description')} onChange={(e) => setV({ ...v, description: e.target.value })} />
      </div>
      {access !== 'leitura' && !disabled && (
        <div className="flex justify-end gap-2 sm:col-span-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setV(fromTask(task))
              onDone()
            }}
          >
            {dirty ? 'Descartar' : 'Fechar edição'}
          </Button>
          <Button type="submit" disabled={!dirty || !valid || pending}>
            <Save /> {pending ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      )}
    </form>
  )
}

function History({ events }: { events: TaskEvent[] }) {
  const name = useMemberName()
  const { areaById } = useSession()
  const describe = (e: TaskEvent): string | null => {
    if (e.kind === 'status_alterado') {
      return `${TASK_STATUS_LABEL[e.from_value as TaskStatus] ?? e.from_value} → ${TASK_STATUS_LABEL[e.to_value as TaskStatus] ?? e.to_value}`
    }
    if (e.kind === 'quantidade_alterada') {
      const f = e.from_value as { prevista: number; entregue: number }
      const t = e.to_value as { prevista: number; entregue: number }
      return `${fmtQty(f.entregue)}/${fmtQty(f.prevista)} → ${fmtQty(t.entregue)}/${fmtQty(t.prevista)}`
    }
    if (e.kind === 'prazo_alterado') return `${(e.from_value as string | null) ?? 'sem prazo'} → ${(e.to_value as string | null) ?? 'sem prazo'}`
    if (e.kind === 'area_alterada') return `${areaById.get(e.from_value as string)?.code ?? '?'} → ${areaById.get(e.to_value as string)?.code ?? '?'}`
    if (e.kind === 'executor_alterado') {
      const t = e.to_value as { id: string | null; text: string | null }
      return `→ ${t.id ? name(t.id) : (t.text ?? 'ninguém')}`
    }
    return null
  }
  if (!events.length) return <p className="text-sm text-muted-foreground">Sem histórico.</p>
  return (
    <ol className="space-y-3 border-l border-border pl-4">
      {events.map((e) => (
        <li key={e.id} className="relative text-sm">
          <span aria-hidden className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-muted-foreground/50" />
          <b className="font-medium">{name(e.actor_id, 'Sistema')}</b> {TASK_EVENT_LABEL[e.kind]}
          {describe(e) && <span className="text-muted-foreground"> · {describe(e)}</span>}
          <div className="font-mono text-[11px] tabular-nums text-muted-foreground">{formatDateTime(e.created_at)}</div>
        </li>
      ))}
    </ol>
  )
}
